// src/lib/onboarding/company-search.ts
/**
 * Company search (onboarding Phase 2, Task 2.1 —
 * docs/superpowers/plans/2026-07-09-startup-member-onboarding.md).
 *
 * Surfaces the claim / create / join matrix (spec §2.2) by combining two
 * sources into one deduped list:
 *
 * 1. `startup_profiles` — real rows, claimed or unclaimed. The baseline RLS
 *    policy "Anyone can view startup profiles" (`FOR SELECT USING (true)`,
 *    `supabase/migrations/00000000000000_baseline.sql`) is unrestricted, so
 *    unclaimed rows (`is_claimed = false`, no owning team yet) ARE visible
 *    to any caller — required so a founder can find and claim them. The
 *    later Task-0.2 migration (`20260709110834_onboarding_rls.sql`) adds a
 *    narrower "team members can view" SELECT policy, but Postgres OR's
 *    same-command RLS policies together, so the permissive baseline policy
 *    still governs — it was intentionally left in place for this reason.
 * 2. The Zefix (Swiss commercial registry) and Startupticker lookups already
 *    used by `startup-essentials.tsx` (`lookupSwissCompany`,
 *    `enrichFromStartupticker`) — reused here, not reimplemented. Matches
 *    with no corresponding `startup_profiles` row are surfaced with
 *    `source: 'registry'` so the UI can offer "create from registry".
 *
 * Registry rows have no real `startup_profiles.id`; `CompanyMatch.id` stays
 * `string` (not nullable) by using a synthetic `registry:<slug>` id — the UI
 * distinguishes real vs. registry matches via `source`, not by null-checking
 * `id`.
 */

import { escapeLikePattern } from '@/lib/sql/escape-like';
import { supabase, isSupabaseConfigured } from '@/lib/supabase/client';
import { lookupSwissCompany, enrichFromStartupticker } from '@/lib/supabase/edge-functions';
import { handleError } from '@/lib/errors';

export interface CompanyMatch {
  id: string;
  name: string;
  is_claimed: boolean;
  source: string; // 'startup_profiles' | 'registry'
}

const MIN_QUERY_LENGTH = 2;
const MAX_DB_RESULTS = 10;

function normalizeName(name: string): string {
  return name.trim().toLowerCase();
}

function registryId(name: string): string {
  return `registry:${normalizeName(name).replace(/\s+/g, '-')}`;
}

/**
 * Searches `startup_profiles` by name (case-insensitive, partial match).
 * Failures are reported and swallowed to an empty array — a registry-only
 * result is still useful, and a DB hiccup shouldn't blank the whole search.
 */
async function searchStartupProfiles(query: string): Promise<CompanyMatch[]> {
  if (!isSupabaseConfigured()) return [];

  try {
    const { data, error } = await supabase
      .from('startup_profiles')
      .select('id, name, is_claimed')
      .ilike('name', `%${escapeLikePattern(query)}%`)
      .order('name', { ascending: true })
      .limit(MAX_DB_RESULTS);

    if (error) throw error;

    return (data ?? []).map((row) => ({
      id: row.id,
      name: row.name,
      is_claimed: row.is_claimed,
      source: 'startup_profiles',
    }));
  } catch (err) {
    handleError(err, { context: 'searchCompanies' });
    return [];
  }
}

/**
 * Zefix + Startupticker lookups, merged into `CompanyMatch[]` with
 * `source: 'registry'`. Each lookup is independent — one failing (or
 * finding nothing) doesn't block the other.
 */
async function searchRegistry(query: string): Promise<CompanyMatch[]> {
  const matches: CompanyMatch[] = [];

  try {
    const zefixResult = await lookupSwissCompany({ name: query });
    const candidates = [
      ...(zefixResult.data?.company ? [zefixResult.data.company] : []),
      ...(zefixResult.data?.results ?? []),
    ];
    for (const candidate of candidates) {
      matches.push({
        id: registryId(candidate.name),
        name: candidate.name,
        is_claimed: false,
        source: 'registry',
      });
    }
  } catch (err) {
    handleError(err, { context: 'searchCompanies' });
  }

  try {
    const tickerResult = await enrichFromStartupticker({ name: query });
    if (tickerResult.data?.found && tickerResult.data.profile) {
      matches.push({
        id: registryId(tickerResult.data.profile.name),
        name: tickerResult.data.profile.name,
        is_claimed: false,
        source: 'registry',
      });
    }
  } catch (err) {
    handleError(err, { context: 'searchCompanies' });
  }

  return matches;
}

/**
 * Searches for companies by name, merging real `startup_profiles` rows
 * (claimed or unclaimed) with registry matches (Zefix/Startupticker) that
 * have no existing page yet. Deduped by normalized name — a
 * `startup_profiles` match wins over a registry match for the same name,
 * since it carries the real `is_claimed` state and id.
 */
export async function searchCompanies(query: string): Promise<CompanyMatch[]> {
  const trimmed = query.trim();
  if (trimmed.length < MIN_QUERY_LENGTH) return [];

  const [dbMatches, registryMatches] = await Promise.all([
    searchStartupProfiles(trimmed),
    searchRegistry(trimmed),
  ]);

  const seen = new Set<string>();
  const results: CompanyMatch[] = [];

  for (const match of [...dbMatches, ...registryMatches]) {
    const key = normalizeName(match.name);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    results.push(match);
  }

  return results;
}
